import { useState, useCallback, useRef } from 'react';
import { CreateOrderSSCCRequest } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';
import { itemUnitsService } from '../../../../../services/itemUnitsService';
import { printingService } from '../../../../../services/printingService';
import { SSCCLabelType } from '../../../../../types/printing';
import { parseDPakBarcode, DPakScanData } from '../../../../../utils/dpakParser';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useAuth } from '../../../../../contexts/AuthContext';
import { ExtendedPickingLine } from './usePickingData';

/**
 * Tidsvindu for duplikatvakten. Identisk råstreng innenfor vinduet regnes som et
 * dobbelt-trigget skann og ignoreres. Vinduet er bevisst kort: flere paller har
 * normalt HELT lik etikett (samme GTIN, batch og dato), så gjentatt skann er en
 * legitim handling — det er bare det utilsiktede dobbeltskuddet vi skal fange.
 */
const DPAK_DUPLICATE_WINDOW_MS = 1000;

export interface DPakQuantityPrompt {
  scan: DPakScanData;
  line: ExtendedPickingLine;
  /** Gjenstående antall på linjen — brukes som forslag i modalen. */
  remaining: number;
}

export interface UseDPakScanDeps {
  /** Fra useAutoCreatePalletOnDPakScan. Er den false gjør hooken ingenting. */
  enabled: boolean;
  pListId: number;
  findLineByItemWithResolve: (scannedItem: string) => Promise<ExtendedPickingLine | null>;
  /**
   * Legger `addedQuantity` til linjens plukkede antall og oppdaterer lokal state.
   *
   * Bevisst IKKE handleUpdateQuantity: den oppretter i tillegg en OrderSSCC-rad mot den
   * aktive pallen (den betjener den manuelle pall-flyten), noe som ville gitt to rader og
   * dobbelttelling per D-pak-skann. Se applyDPakPickedQuantity i ModernPickingDetails.
   */
  applyPickedQuantity: (line: ExtendedPickingLine, addedQuantity: number) => Promise<void>;
  loadPallets: () => Promise<void>;
  /** Valgt SSCC-rapport (ReportType 50). Udefinert => backend bruker innebygd standard. */
  ssccReportId?: number;
}

export interface UseDPakScanReturn {
  /**
   * Forsøker å behandle et råskann som D-pak.
   * Returnerer true når skannet er konsumert (kalleren skal stoppe), false når det
   * skal falle gjennom til vanlig plukkflyt.
   */
  tryHandleDPakScan: (raw: string) => Promise<boolean>;
  quantityPrompt: DPakQuantityPrompt | null;
  confirmQuantity: (quantity: number) => Promise<void>;
  cancelQuantity: () => void;
  isProcessing: boolean;
}

/**
 * D-pak-skann → automatisk pall (beslutning 1-15, se «Claude md files/D-Pak-Pall-Nivaa3/Plan.md»).
 *
 * Et skann som inneholder GTIN sammen med batch og/eller holdbarhet tolkes som en
 * D-pak-etikett: Compass genererer SSCC, oppretter OrderSSCC-raden med batch,
 * holdbarhet og antall fra etiketten, øker plukket antall og printer pall-etiketten.
 *
 * Hooken er bevisst en ren ORKESTRERING av eksisterende byggeklosser
 * (generateSscc, createOrUpdateSSCC, updatePickedQuantity, printSSCCLabel) og
 * ligger UTENFOR handleScan, slik at dagens skann-flyt er uendret når innstillingen
 * er av eller skannet ikke er en D-pak.
 */
export function useDPakScan(deps: UseDPakScanDeps): UseDPakScanReturn {
  const {
    enabled,
    pListId,
    findLineByItemWithResolve,
    applyPickedQuantity,
    loadPallets,
    ssccReportId,
  } = deps;

  const { t } = useTranslation();
  const { user } = useAuth();

  const [quantityPrompt, setQuantityPrompt] = useState<DPakQuantityPrompt | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Duplikatvakt + re-entrancy-lås. Refs (ikke state) fordi verdiene må leses
  // synkront i samme tick som skannet kommer inn.
  const lastScanRef = useRef<{ raw: string; ts: number } | null>(null);
  const processingRef = useRef(false);
  // Speiler quantityPrompt slik at skann kan avvises synkront mens antallsmodalen står
  // åpen. Da slipper vi å utvide isAnyModalOpen i ModernPickingDetails (som ville krevd
  // at hooken kalles før den er definert).
  const promptOpenRef = useRef(false);
  // Printfeil varsles kun ÉN gang per økt. Uten printeroppsett feiler hver eneste
  // utskrift, og en dialog per pall ville gjort flyten ubrukelig.
  const printWarningShownRef = useRef(false);

  const warnPrintFailedOnce = useCallback((detail?: string) => {
    console.warn('D-pak: SSCC-etikett ble ikke skrevet ut.', detail ?? '');
    if (printWarningShownRef.current) return;
    printWarningShownRef.current = true;
    window.alert(t(
      'warehouse.modules.picking.alerts.dpakPrintFailed',
      { '0': detail || '' },
      'Etiketten kunne ikke skrives ut ({0}). Pallene registreres som normalt — bruk «Skriv ut» i pallpanelet. Denne meldingen vises kun én gang.'
    ));
  }, [t]);

  /**
   * Registrerer én D-pak: ny pall + OrderSSCC-rad + økt QtyPicked + etikett.
   * Overplukk-vakt: er linjen full (rest 0) avvises skannet. Er D-pakken større
   * enn resten, klemmes antallet STILLE ned til resten og det opprettes en
   * restpall (sak 36340502 pkt. 2) — batch/holdbarhet fra samme etikett, ingen
   * dialog. Grunnlaget kan dermed aldri overstige bestilt antall. Gjelder både
   * etikett-antall og manuelt oppgitt antall (samme vakt for begge løp).
   */
  const registerDPak = useCallback(async (
    scan: DPakScanData,
    line: ExtendedPickingLine,
    quantity: number
  ): Promise<void> => {
    const remaining = line.qty - line.qtyPicked;

    if (remaining <= 0) {
      window.alert(t(
        'warehouse.modules.picking.alerts.dpakOverpick',
        { '0': String(quantity), '1': String(remaining) },
        'D-pak inneholder {0}, men bare {1} gjenstår på linjen. Skannet ble ikke registrert.'
      ));
      return;
    }

    if (quantity > remaining) {
      console.log(`📦 D-pak: restpall — etiketten sier ${quantity}, ${remaining} gjenstår; registrerer ${remaining}`);
      quantity = remaining;
    }

    // Ekte SSCC er et krav her — etiketten skal bære en gyldig GS1-kode. Vi faller
    // derfor IKKE tilbake til en lokal pall-ID slik den manuelle flyten gjør.
    const sscc = await pickingService.generateSscc();
    if (!sscc) {
      window.alert(t(
        'warehouse.modules.picking.alerts.dpakSsccNotConfigured',
        {},
        'SSCC er ikke satt opp for firmaet. Kontakt systemansvarlig før D-pak-skanning tas i bruk.'
      ));
      return;
    }

    // OBS: OrderSSCC.LineNo skal være ORDRELINJENS LineNo — eksporten joiner
    // OrderLine.LineNo = OrderSSCC.LineNo. Samme valg som createNewPallet.
    const ssccRequest: CreateOrderSSCCRequest = {
      companyNo: '', // settes av backend
      pListID: pListId,
      orderNo: line.orderNo,
      lineNo: line.lineNo,
      sscc,
      gtin: scan.gtin || undefined,
      quantity,
      ssccLevel: 1,
      itemBatchNo: scan.batchNo || '',
      itemBatchExpDate: scan.expiryDate || null,
      stackingWeight: 0,
      grossWeight: 0,
      locationName: line.binCode || line.location,
    };

    await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
    await applyPickedQuantity(line, quantity);

    // loadPallets viser den nye pallen i pallpanelet. Vi setter den bevisst IKKE som
    // aktiv pall: en D-pak-pall er komplett i det den opprettes, og en aktiv pall ville
    // fanget opp etterfølgende VANLIGE vareplukk (handleUpdateQuantity legger dem på den
    // aktive pallen). I blandede ordrer ville det gitt feil pallinnhold.
    await loadPallets();

    // Etiketten printes til slutt. Feiler den, står plukket likevel — operatøren bruker
    // «Skriv ut» i pallpanelet. Vi ruller altså ikke tilbake registreringen.
    // Merk at backend returnerer success=false (ikke unntak) når printer mangler, så begge
    // utfall må håndteres.
    try {
      if (user?.companyNo) {
        const result = await printingService.printSSCCLabel(user.companyNo, {
          sscc,
          pListId,
          reportId: ssccReportId,
          quantity,
          labelType: SSCCLabelType.StandardPallet,
          gtin: scan.gtin || undefined,
          batchNo: scan.batchNo || undefined,
          expiryDate: scan.expiryDate || undefined,
        });

        if (!result?.success) {
          warnPrintFailedOnce(result?.message);
        }
      }
    } catch (err) {
      console.error('D-pak: SSCC-etikett kunne ikke skrives ut:', err);
      warnPrintFailedOnce(err instanceof Error ? err.message : undefined);
    }
  }, [pListId, applyPickedQuantity, loadPallets, ssccReportId, user, t, warnPrintFailedOnce]);

  const tryHandleDPakScan = useCallback(async (raw: string): Promise<boolean> => {
    if (!enabled) return false;

    const scan = parseDPakBarcode(raw);
    if (!scan.isDPak || !scan.gtin) {
      // Ikke en D-pak — la vanlig plukkflyt håndtere skannet (blandede ordrer).
      return false;
    }

    // Duplikatvakt: samme råstreng innen vinduet er et dobbelt-trigget skann.
    const now = Date.now();
    const last = lastScanRef.current;
    if (last && last.raw === raw && now - last.ts < DPAK_DUPLICATE_WINDOW_MS) {
      console.log('📦 D-pak: identisk skann innen duplikatvinduet — ignorert');
      return true;
    }

    // Re-entrancy: et nytt skann mens forrige registreres skal ikke gi to paller.
    if (processingRef.current || promptOpenRef.current) {
      console.log('📦 D-pak: behandling pågår — skann ignorert');
      return true;
    }

    lastScanRef.current = { raw, ts: now };
    processingRef.current = true;
    setIsProcessing(true);

    try {
      const line = await findLineByItemWithResolve(scan.gtin);
      if (!line) {
        window.alert(t('warehouse.modules.picking.alerts.noLineMatchesGtin', { '0': scan.gtin }));
        return true;
      }

      // Antall: etikett → enhetsfaktor på varens strekkode → spør operatøren.
      let quantity = scan.quantity;

      if (!quantity) {
        try {
          const unit = await itemUnitsService.resolveByBarcode(scan.gtin);
          if (unit?.unitFactor && unit.unitFactor > 0) {
            quantity = unit.unitFactor;
          }
        } catch (err) {
          console.warn('D-pak: oppslag av enhetsfaktor feilet, spør operatøren i stedet:', err);
        }
      }

      if (!quantity) {
        promptOpenRef.current = true;
        setQuantityPrompt({ scan, line, remaining: line.qty - line.qtyPicked });
        return true;
      }

      await registerDPak(scan, line, quantity);
      return true;
    } catch (err) {
      console.error('D-pak: registrering feilet:', err);
      window.alert(t(
        'warehouse.modules.picking.alerts.dpakRegisterFailed',
        {},
        'Kunne ikke registrere D-pak-plukket. Prøv igjen.'
      ));
      return true;
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  }, [enabled, findLineByItemWithResolve, registerDPak, t]);

  const confirmQuantity = useCallback(async (quantity: number): Promise<void> => {
    const prompt = quantityPrompt;
    if (!prompt) return;

    setQuantityPrompt(null);
    promptOpenRef.current = false;
    processingRef.current = true;
    setIsProcessing(true);
    try {
      await registerDPak(prompt.scan, prompt.line, quantity);
    } catch (err) {
      console.error('D-pak: registrering etter antallsbekreftelse feilet:', err);
      window.alert(t(
        'warehouse.modules.picking.alerts.dpakRegisterFailed',
        {},
        'Kunne ikke registrere D-pak-plukket. Prøv igjen.'
      ));
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  }, [quantityPrompt, registerDPak, t]);

  const cancelQuantity = useCallback(() => {
    setQuantityPrompt(null);
    promptOpenRef.current = false;
  }, []);

  return {
    tryHandleDPakScan,
    quantityPrompt,
    confirmQuantity,
    cancelQuantity,
    isProcessing,
  };
}
